import React, { useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'

import { TableUrlPagination } from '../../../../ui'
import { eeppDataPreparation } from '../../logics'
import { EEPP_TABLE_COLUMNS } from '../../constants'
import { useRowSelector } from '../../../loader/hooks'

const getLookup = (combo) => {
  let lookup = {}
  if (Array.isArray(combo)) {
    combo.forEach((item) => (lookup[item.key] = item.value))
  }
  return lookup
}

export const EEPPTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const navigate = useNavigate()
  const location = useLocation()
  const role = useSelector((state) => state.global.role)
  const eeppLoader = useSelector((state) => state.loader.eepp)
  const combos = useSelector((state) => state.combos)

  const data = eeppLoader?.data?.empresas || []
  const totalEntries = eeppLoader?.data?.total_empresas
  const search = eeppLoader?.config?.params
  const loading = eeppLoader?.loading

  const { selection, addSelection } = useRowSelector({
    identifier: 'eepp',
    selectFilter: (state) => state?.data?.empresas,
    idField: 'empresa_ca_id',
  })

  useEffect(() => {
    if (selection.length) {
      addSelection([])
    }
  }, [])

  const handleGoToDetail = (_, row) => {
    if (!row) return

    const props = {
      pathname: `/eepp/${row.empresa_id}`,
      search: row.empresa_ca_id ? `?ca=${row.empresa_ca_id}` : '',
      state: { prevSearch: location?.search || '' },
    }
    return navigate(props)
  }

  const handleSelectRows = useCallback(
    (rows) => {
      addSelection(rows)
    },
    [addSelection]
  )

  const formattedColumns = useMemo(() => {
    return EEPP_TABLE_COLUMNS.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        header: formatMessage({ id: `pages.eepp.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.width,
        maxSize: column.width,
        muiTableBodyCellProps: {
          style: {
            padding: '0 16px',
          },
        },
      }

      if (column.field === 'actions') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.actions' }),
          Cell: () => <span>Acciones</span>,
          muiTableBodyCellProps: { align: 'center' },
        }
      }

      const comboRaw = combos?.[column.comboId]
      const comboArray = Array.isArray(comboRaw) ? comboRaw : comboRaw?.data

      if (column.comboId && Array.isArray(comboArray)) {
        const lookup = getLookup(comboArray)

        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const val = cell.getValue()
            if (!val && val !== 0) return <span>--</span>

            const strVal = String(val)
            const numVal = Number(val)

            const translatedText =
              lookup[strVal] ??
              lookup[numVal] ??
              lookup[String(numVal)] ??
              val

            return <span>{translatedText ?? '--'}</span>
          },
        }
      }

      return {
        ...baseColumn,
        Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
      }
    })
  }, [combos, formatMessage])

  const processedData = useMemo(() => eeppDataPreparation(data, { intl }), [data, intl])

  const finalTableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: role === 'e_commerce',
      draggable: false,
    }),
    [role]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={finalTableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
      onRowClick={handleGoToDetail}
    />
  )
}